import React, { useState } from 'react';
import { Edit2, Trash2, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AddDeliverableModal } from './AddDeliverableModal';
import { EditDeliverableModal } from './EditDeliverableModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { useEventDeliverables, useDeleteEventDeliverable, type EventDeliverable } from '../../hooks/useEventDeliverables';
import { format } from 'date-fns';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { StatusBadge } from '@/components/status/StatusBadge';

export interface EventDeliverablesTabProps {
  eventId: string;
}

export const EventDeliverablesTab: React.FC<EventDeliverablesTabProps> = ({ eventId }) => {
  const [editingDeliverable, setEditingDeliverable] = useState<EventDeliverable | null>(null);
  const [isDeliverableDeleteDialogOpen, setIsDeliverableDeleteDialogOpen] = useState(false);
  const [deliverableToDelete, setDeliverableToDelete] = useState<{ id: string; influencer_name: string } | null>(null);

  const { toast } = useToast();
  const { data: eventDeliverables, isLoading: deliverablesLoading } = useEventDeliverables(eventId);
  const deleteEventDeliverable = useDeleteEventDeliverable();

  const deliverableCounts = {
    inProgress: eventDeliverables?.filter(d => d.status === "In Progress").length || 0,
    delivered: eventDeliverables?.filter(d => d.status === "Delivered").length || 0,
    pendingApproval: eventDeliverables?.filter(d => d.status === "Pending Approval").length || 0,
    approved: eventDeliverables?.filter(d => d.status === "Approved").length || 0,
    revisionNeeded: eventDeliverables?.filter(d => d.status === "Revision Needed").length || 0,
  };

  const getPlatformColor = (platform: string) => {
    switch (platform.toLowerCase()) {
      case "instagram": return "bg-pink-100 text-pink-800";
      case "youtube": return "bg-red-100 text-red-800";
      case "tiktok": return "bg-black text-white";
      case "twitter": return "bg-blue-100 text-blue-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "MMM dd, yyyy");
  };

  const handleDeleteDeliverable = async () => {
    if (!deliverableToDelete || !eventId) return;
    try {
      await deleteEventDeliverable.mutateAsync({
        id: deliverableToDelete.id,
        eventId: eventId,
      });
      setIsDeliverableDeleteDialogOpen(false);
      setDeliverableToDelete(null);
      toast({ title: "Success", description: "Deliverable deleted successfully" });
    } catch (error) {
      console.error('Failed to delete deliverable:', error);
      toast({ title: "Error", description: "Failed to delete deliverable. Please try again.", variant: "destructive" });
    }
  };

  return (
    <>
      <Card className="bg-white shadow-lg border-0">
        <CardHeader>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <CardTitle className="text-xl font-bold text-gray-900">Event Deliverables</CardTitle>
              <p className="text-gray-600 mt-1">Track content deliverables from influencers</p>
            </div>
            <AddDeliverableModal eventId={eventId} />
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-5">
            <Card className="text-center p-4">
              <div className="text-2xl font-bold text-orange-600">{deliverableCounts.inProgress}</div>
              <div className="text-sm text-gray-600">In Progress</div>
            </Card>
            <Card className="text-center p-4">
              <div className="text-2xl font-bold text-blue-600">{deliverableCounts.delivered}</div>
              <div className="text-sm text-gray-600">Delivered</div>
            </Card>
            <Card className="text-center p-4">
              <div className="text-2xl font-bold text-yellow-600">{deliverableCounts.pendingApproval}</div>
              <div className="text-sm text-gray-600">Pending Approval</div>
            </Card>
            <Card className="text-center p-4">
              <div className="text-2xl font-bold text-green-600">{deliverableCounts.approved}</div>
              <div className="text-sm text-gray-600">Approved</div>
            </Card>
            <Card className="text-center p-4">
              <div className="text-2xl font-bold text-red-600">{deliverableCounts.revisionNeeded}</div>
              <div className="text-sm text-gray-600">Revision Needed</div>
            </Card>
          </div>

          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Deliverables ({eventDeliverables?.length || 0})
            </h3>
            <p className="text-sm text-gray-600 mb-4">Content deliverables tracking</p>
          </div>

          {deliverablesLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="medium" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Influencer</TableHead>
                  <TableHead>Platform</TableHead>
                  <TableHead>Expected Deliverable</TableHead>
                  <TableHead>Deadline</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Content</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {eventDeliverables?.map((deliverable) => (
                  <TableRow key={deliverable.id} className="hover:bg-gray-50">
                    <TableCell>
                      <div className="font-semibold text-gray-900">{deliverable.influencer_name}</div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getPlatformColor(deliverable.platform) + ' text-xs'}>
                        {deliverable.platform}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-900">
                      {deliverable.expected_deliverable}
                    </TableCell>
                    <TableCell className="text-gray-900">
                      {deliverable.deadline ? formatDate(deliverable.deadline) : "-"}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={deliverable.status} className="text-[11px]" />
                    </TableCell>
                    <TableCell>
                      {deliverable.content_url ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-blue-600 hover:text-blue-800"
                          onClick={() => window.open(deliverable.content_url!, '_blank')}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View Content
                        </Button>
                      ) : (
                        <span className="text-gray-500 text-sm">No content</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600 max-w-xs truncate">
                      {deliverable.notes || "-"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setEditingDeliverable(deliverable)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-800"
                          onClick={() => {
                            setDeliverableToDelete({ id: deliverable.id, influencer_name: deliverable.influencer_name });
                            setIsDeliverableDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {!deliverablesLoading && (!eventDeliverables || eventDeliverables.length === 0) && (
            <div className="text-center py-8">
              <p className="text-gray-500">No deliverables found for this event.</p>
            </div>
          )}
        </CardContent>
      </Card>

      <EditDeliverableModal
        isOpen={!!editingDeliverable}
        onClose={() => setEditingDeliverable(null)}
        deliverable={editingDeliverable}
      />

      <AlertDialog open={isDeliverableDeleteDialogOpen} onOpenChange={setIsDeliverableDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the deliverable for "{deliverableToDelete?.influencer_name}".
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeliverableToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteDeliverable} className="bg-red-600 hover:bg-red-700 text-white">
              Delete Deliverable
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
